/**
 * The imperative API `EditorV2` hands its host on mount (`onReady`).
 *
 * A host does not own the v2 editor's engine handle — the package keeps it private, so that
 * ProseMirror and Yjs never reach a host through this seam. But a host DOES own the surfaces
 * around the document: an AI panel, a comment thread, a skill card, a Taskize tray. Those need
 * four things and no more — read the selection, write at a range, know where the document ends,
 * and re-find a remembered span by its text — which is exactly what this exposes, in plain
 * shapes. Nothing here needs the engine's own types, so nothing here has them.
 *
 * ── Why this file exists as a factory ────────────────────────────────────────────────────
 * Everything here reads the LIVE handle at CALL time (`deps.getHandle()`), so a host may keep
 * one api object for the lifetime of its mount and never care about registration order or a
 * remount. Building it as a factory over that accessor also makes it unit-testable against a
 * fake handle, which is how the range/lane/author mapping below is actually proven.
 *
 * Backwards compatibility: `insertObjects` is unchanged and remains the first member of the
 * interface. knowit-main (React 17) types its `onReady` callback as `{ insertObjects }`, which
 * a widened api still satisfies.
 */

import type { EditorV2Handle, Lane, ParticipantRef, SelectionAnchor } from "@docit/editor-core";

/** A live-object card the host wants materialized in the document. */
export interface HostInsertObject {
  kind: string;
  summary: string;
  data?: Record<string, unknown>;
}

/** Who a host-driven write is attributed to. Defaults to an unnamed AI participant. */
export interface HostWriteAuthor {
  kind: "ai" | "human";
  /** Stable id (`thinker:<slug>`, `user:<uuid>`); defaults to the acting human / `ai:host`. */
  id?: string;
  displayName?: string;
  /** Model id for AI participants — rendered in the attribution chrome. */
  model?: string;
}

export interface HostApplyOptions {
  /** Insert at `range.from` and leave the range intact — "add this", not "rewrite this". */
  insertOnly?: boolean;
  author?: HostWriteAuthor;
  /** Human-readable why; travels onto the proposal if the lane routes it to review. */
  rationale?: string;
}

/**
 * What a host-driven write actually did. `applied:false` with a `proposalId` is a SUCCESS that
 * is not in the document yet — the lane policy routed it to review — and is deliberately
 * distinguishable from `applied:false` with a `reason`, which is a refusal.
 */
export interface HostApplyResult {
  applied: boolean;
  /** The lane the engine resolved; absent when the write never reached the engine. */
  lane?: Lane;
  proposalId?: string;
  reason?: string;
}

/**
 * Small imperative API handed to the host once the editor is ready (AI Surfaces v2, W6.1).
 * Every member reads the live editor at call time and answers honestly when there is none.
 */
export interface EditorV2HostApi {
  /** Insert live-object cards (task/decision/…) at the cursor, each as one attributed act. */
  insertObjects(objects: HostInsertObject[]): void;
  /**
   * The current selection as a writer-back anchor — text, engine positions, plain-text
   * offsets and 400-character context windows — or `null` when nothing is selected, which is
   * a state and not a failure.
   */
  getSelection(): SelectionAnchor | null;
  /**
   * Write `text` over `range` (or at the document end when `range` is null) as ONE attributed
   * transaction through the engine's single write path.
   *
   * The payload is plain text: `\n`-separated content lands as real paragraphs, and markdown
   * marks are NOT parsed — editor-core carries a markdown projection but no markdown parser,
   * so this behaves exactly like every AI action the bubble bar and the prompt dock already
   * run. New text is attributed and PROVISIONAL until somebody commits it, which is the whole
   * point of this lane: a host write is visible as a host write.
   */
  applyAtRange(
    range: { from: number; to: number } | null,
    text: string,
    options?: HostApplyOptions,
  ): HostApplyResult;
  /** The end of the document, as a position an append can be applied at. 0 with no editor. */
  documentEnd(): number;
  /**
   * Re-find a span by its TEXT: the range it occupies NOW, or `null` when the text is no
   * longer there. A surface that stored a quotation — a comment thread, a saved anchor —
   * re-locates it by content, so a position captured earlier can never overwrite the wrong
   * words: if the text changed, the answer is null and the caller refuses.
   */
  findText(text: string): { from: number; to: number } | null;
}

/** Document identity + containment chain + the acting human, read at call time. */
export interface HostApiContext {
  documentId: string;
  scope?: { workspaceId?: string; projectId?: string; collectionId?: string };
  currentUserId: string;
  currentUserName?: string;
}

export interface HostApiDeps {
  /** The live engine handle, or null before mount / after unmount. */
  getHandle(): EditorV2Handle | null;
  getContext(): HostApiContext;
}

/**
 * How much text either side of the selection a returned anchor carries. 400 is the window a
 * document anchor stores (slate-feature-transfer W8 §B.5), so a host that persists this
 * anchor and one that re-reads it agree about how much context "context" is.
 */
export const ANCHOR_CONTEXT_WINDOW = 400;

/** The insert position meaning "the end of the document", snapped by the engine. */
const DOC_END = Number.MAX_SAFE_INTEGER;

/** Resolve the participant a host write is attributed to (AI unless told otherwise). */
function participantFor(
  author: HostWriteAuthor | undefined,
  context: HostApiContext,
): ParticipantRef {
  if (author?.kind === "human") {
    return {
      kind: "human",
      id: author.id ?? context.currentUserId,
      displayName: author.displayName ?? context.currentUserName ?? "You",
      ...(author.model ? { model: author.model } : {}),
    };
  }
  return {
    kind: "ai",
    // A host write with no named agent is still an AI write — never silently the human's.
    id: author?.id ?? "ai:host",
    displayName: author?.displayName ?? "AI",
    ...(author?.model ? { model: author.model } : {}),
  };
}

export function createHostApi(deps: HostApiDeps): EditorV2HostApi {
  return {
    insertObjects: (objects) => {
      const handle = deps.getHandle();
      if (!handle) return;
      for (const o of objects) handle.insertObject(o.kind, o.summary, o.data);
      handle.focus();
    },

    getSelection: () => deps.getHandle()?.getSelectionAnchor(ANCHOR_CONTEXT_WINDOW) ?? null,

    documentEnd: () => deps.getHandle()?.insertAt(DOC_END).from ?? 0,

    findText: (text) => deps.getHandle()?.findText(text) ?? null,

    applyAtRange: (range, text, options) => {
      const handle = deps.getHandle();
      if (!handle) {
        return { applied: false, reason: "The editor is not ready." };
      }
      const payload = text.trim();
      // An empty payload applied over a range is a silent DELETION of that range — the one
      // outcome nobody asked for. Refuse, exactly as the classic lane's apply does.
      if (payload.length === 0) {
        return { applied: false, reason: "Nothing to write." };
      }
      const context = deps.getContext();
      const target = range ?? handle.insertAt(DOC_END);
      const result = handle.dispatchEdit({
        participant: participantFor(options?.author, context),
        // Ask for the live lane; the resolver's most-restrictive floor may still route it to
        // review, and the result says which happened rather than pretending it applied.
        requestedLane: "act",
        range: target,
        op: options?.insertOnly ? "insert" : "replace",
        text: payload,
        ...(options?.rationale ? { rationale: options.rationale } : {}),
        scope: { ...context.scope, documentId: context.documentId },
      });
      return {
        applied: result.applied,
        lane: result.lane,
        ...(result.proposal ? { proposalId: result.proposal.proposalId } : {}),
        ...(result.reason ? { reason: result.reason } : {}),
      };
    },
  };
}
